/**
 * Monotone cubic interpolation (Steffen's method) → SVG path strings.
 *
 * Why monotone and not plain Catmull-Rom: trend data spikes; a Catmull-Rom
 * spline overshoots past the real min/max at every spike, drawing values that
 * don't exist. Monotone-X (what d3's curveMonotoneX does) stays smooth but
 * never over/undershoots the data — smooth AND honest.
 *
 * Mirrored in mobile/src/lib/curve.ts (separate package root, same algorithm).
 */

export interface XY {
  x: number
  y: number
}

/** Per-point tangent slopes, sign-preserving (Steffen 1990). */
function slopes(pts: XY[]): number[] {
  const n = pts.length
  const m = new Array<number>(n).fill(0)
  if (n < 2) return m
  const h: number[] = []
  const s: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const dx = Math.max(pts[i + 1].x - pts[i].x, 1e-9)
    h.push(dx)
    s.push((pts[i + 1].y - pts[i].y) / dx)
  }
  m[0] = s[0]
  m[n - 1] = s[n - 2]
  for (let i = 1; i < n - 1; i++) {
    if (s[i - 1] * s[i] <= 0) {
      m[i] = 0 // local extremum — flat tangent, no overshoot
    } else {
      const p = (s[i - 1] * h[i] + s[i] * h[i - 1]) / (h[i - 1] + h[i])
      m[i] =
        (Math.sign(s[i - 1]) + Math.sign(s[i])) *
        Math.min(Math.abs(s[i - 1]), Math.abs(s[i]), 0.5 * Math.abs(p))
    }
  }
  return m
}

const f = (v: number) => (Math.round(v * 100) / 100).toString()

/** Smooth open line through the points. Empty string when < 2 points. */
export function monotonePathD(pts: XY[]): string {
  if (pts.length < 2) return ''
  const m = slopes(pts)
  let d = `M${f(pts[0].x)} ${f(pts[0].y)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]
    const b = pts[i + 1]
    const h = (b.x - a.x) / 3
    d += `C${f(a.x + h)} ${f(a.y + m[i] * h)} ${f(b.x - h)} ${f(b.y - m[i + 1] * h)} ${f(b.x)} ${f(b.y)}`
  }
  return d
}

/** The same curve closed down to `baseY` — the area fill under the line. */
export function monotoneAreaD(pts: XY[], baseY: number): string {
  const line = monotonePathD(pts)
  if (!line) return ''
  const first = pts[0]
  const last = pts[pts.length - 1]
  return `${line}L${f(last.x)} ${f(baseY)}L${f(first.x)} ${f(baseY)}Z`
}
